<template>
  <div
    ref="barChannel"
    class="mc-video-history-new__bar-channel-item"
    @mousemove="handleBarHover"
    @mouseleave="handleBarLeave"
    @click="handleBarClick"
    @dblclick="handleBarDoubleClick"
  >
    <portal to="video-history-bar-tooltips">
      <div
        v-if="hoveredTime"
        class="mc-video-history-new__bar-tooltip mc-video-history-new__bar-tooltip--fixed"
        :style="{
          left: `${tooltipPosition}px`,
          top: `${tooltipTop}px`,
        }"
      >
        {{ hoveredTime }}
      </div>
    </portal>

    <b-skeleton v-if="loadingFilelist" width="100%" />
    <canvas
      v-else
      ref="canvas"
      class="mc-video-history-new__bar-channel-canvas"
      :style="canvasStyle"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import moment from 'moment'
import { Helper } from '@/mixin/helper'
import { getCanvasBarRanges } from '../../../helper/canvas-bar-ranges'

moment.locale('id')

export default {
  mixins: [Helper],

  props: {
    channel: { type: Number, required: true },
    isChannelActive: { type: Boolean, required: true },
    startTimeBuffered: { type: String, default: '' },
    endTimeBuffered: { type: String, default: '' },
    timelineWidth: { type: Number, default: 0 },
    viewportStart: { type: Number, default: 0 },
    viewportWidth: { type: Number, default: 0 },
    singleClickDelayMs: {
      type: Number,
      default: 250,
    },
  },

  emits: ['clickBarDatetime', 'doubleClickBarDatetime'],

  data() {
    return {
      tooltipPosition: 0,
      tooltipTop: 0,
      hoveredTime: null,
      hoveredDatetimeUtc: null,
      canvasRenderFrame: null,
      singleClickTimer: null,
      minimumVisibleWidthPx: 2,
      channelColor: '#1c74da',
      channelBorderColor: '#e8f1fb',
    }
  },

  computed: {
    ...mapGetters('videoHistory', ['periodInSeconds', 'deviceTimezone']),
    ...mapState('videoHistory', ['mapFilelist', 'period', 'loadingFilelist']),
    filelist() {
      return (this.mapFilelist[this.channel] ?? []).map(({ startTime, endTime }) => ({
        startTime: this.getUtcDateFromLocalDate(startTime, this.deviceTimezone),
        endTime: this.getUtcDateFromLocalDate(endTime, this.deviceTimezone),
      }))
    },
    canvasWidth() {
      return Math.max(Math.min(this.viewportWidth, this.timelineWidth - this.viewportStart), 0)
    },
    canvasStyle() {
      return {
        left: `${this.viewportStart}px`,
        width: `${this.canvasWidth}px`,
      }
    },
    canvasRenderState() {
      return {
        filelist: this.filelist,
        period: this.period,
        timelineWidth: this.timelineWidth,
        viewportStart: this.viewportStart,
        canvasWidth: this.canvasWidth,
        isChannelActive: this.isChannelActive,
        startTimeBuffered: this.startTimeBuffered,
        endTimeBuffered: this.endTimeBuffered,
      }
    },
  },

  watch: {
    loadingFilelist(isLoading) {
      if (isLoading) return

      this.$nextTick(this.scheduleCanvasRender)
    },
    canvasRenderState: {
      deep: true,
      immediate: true,
      handler() {
        this.scheduleCanvasRender()
      },
    },
  },

  mounted() {
    this.scheduleCanvasRender()
  },

  beforeDestroy() {
    if (this.canvasRenderFrame !== null) window.cancelAnimationFrame(this.canvasRenderFrame)
    if (this.singleClickTimer !== null) window.clearTimeout(this.singleClickTimer)
  },

  methods: {
    scheduleCanvasRender() {
      if (this.canvasRenderFrame !== null) window.cancelAnimationFrame(this.canvasRenderFrame)

      this.canvasRenderFrame = window.requestAnimationFrame(() => {
        this.canvasRenderFrame = null
        this.renderCanvas()
      })
    },

    renderCanvas() {
      const canvas = this.$refs.canvas
      const height = this.$refs.barChannel?.clientHeight ?? 0
      if (!canvas || !this.canvasWidth || !height) return

      const pixelRatio = window.devicePixelRatio || 1
      canvas.width = Math.ceil(this.canvasWidth * pixelRatio)
      canvas.height = Math.ceil(height * pixelRatio)

      const context = canvas.getContext('2d')
      if (!context) return

      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0)
      context.clearRect(0, 0, this.canvasWidth, height)

      const ranges = getCanvasBarRanges({
        filelist: this.filelist,
        periodStart: this.period[0],
        periodEnd: this.period[1],
        timelineWidth: this.timelineWidth,
        viewportStart: this.viewportStart,
        viewportWidth: this.canvasWidth,
        minimumVisibleWidthPx: this.minimumVisibleWidthPx,
        bufferedStart: this.startTimeBuffered,
        bufferedEnd: this.endTimeBuffered,
      })

      context.fillStyle = this.isChannelActive
        ? 'rgba(28, 116, 218, 0.75)'
        : 'rgba(28, 116, 218, 0.5)'
      context.strokeStyle = this.channelBorderColor
      context.lineWidth = 1

      ranges.forEach(({ x, width }) => {
        context.fillRect(x, 0, width, height)
        context.strokeRect(x + 0.5, 0.5, Math.max(width - 1, 0), Math.max(height - 1, 0))
      })

      context.fillStyle = this.channelColor
      ranges.forEach(({ buffered }) => {
        if (buffered) context.fillRect(buffered.x, 0, buffered.width, height)
      })
    },

    handleBarHover(event) {
      if (!this.period[0] || !this.periodInSeconds) return

      const bar = this.$refs.barChannel
      if (!bar) return

      const rect = bar.getBoundingClientRect()
      const cursorX = Math.min(Math.max(event.clientX - rect.left, 0), rect.width)
      const startTimestamp = moment(this.period[0]).valueOf()
      const endTimestamp = moment(this.period[1]).valueOf()
      const duration = endTimestamp - startTimestamp

      if (!Number.isFinite(duration) || duration <= 0) {
        this.hoveredTime = null
        return
      }

      const tooltipHalfWidth = 75
      this.tooltipPosition = Math.min(
        Math.max(event.clientX, tooltipHalfWidth),
        Math.max(window.innerWidth - tooltipHalfWidth, tooltipHalfWidth)
      )
      this.tooltipTop = rect.top - 8

      const hoveredTimestamp = startTimestamp + duration * (cursorX / rect.width)
      this.hoveredDatetimeUtc = moment
        .utc(hoveredTimestamp)
        .utcOffset(Number(this.deviceTimezone) * 60)
        .toISOString()
      this.hoveredTime = moment
        .utc(hoveredTimestamp)
        .utcOffset(Number(this.deviceTimezone) * 60)
        .format('YYYY-MM-DD HH:mm:ss')
    },

    handleBarClick() {
      if (!this.hoveredDatetimeUtc) return

      const datetime = this.hoveredDatetimeUtc

      if (this.singleClickTimer !== null) {
        window.clearTimeout(this.singleClickTimer)
      }

      this.singleClickTimer = window.setTimeout(() => {
        this.singleClickTimer = null
        this.$emit('clickBarDatetime', datetime)
      }, this.singleClickDelayMs)
    },

    handleBarDoubleClick() {
      if (this.singleClickTimer !== null) {
        window.clearTimeout(this.singleClickTimer)
        this.singleClickTimer = null
      }

      if (!this.hoveredDatetimeUtc) return

      this.$emit('doubleClickBarDatetime', this.hoveredDatetimeUtc)
    },

    handleBarLeave() {
      this.hoveredTime = null
      this.hoveredDatetimeUtc = null
    },
  },
}
</script>
